import { Brain, Globe, Landmark, Mail, NotebookPen, Phone } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { PilotFact, PilotFactSource } from "../../../lib/callPilot";

/* Typed as the FULL record, not a Partial, so a source added to the payload
   without a glyph here is a compile error rather than the fallback below
   quietly dressing the brain's verdict as a scanner finding. The badge is the
   line between "our scanner saw this" and "a model concluded this", and the
   operator is about to repeat it out loud. */
const SOURCE_ICON: Record<PilotFactSource, typeof Globe> = {
  scanner: Globe,
  register: Landmark,
  history: Phone,
  mail: Mail,
  brain: Brain,
};

/**
 * The known facts, each wearing where it came from.
 *
 * Lifted out of the co-pilot's own panel when the dial pad started drawing the
 * same list. The badge is not decoration: it is the line between "our scanner
 * saw this on their site" and "a model said it", and the operator is about to
 * repeat it out loud. Two copies of this markup would eventually be two answers
 * to that question, which is the one place this list must not drift.
 *
 * `onAddToNote` is optional because the two callers are at different moments.
 * Mid-call there is a note to write into; at the dial pad there is no call yet,
 * and a button that wrote somewhere invisible would be worse than no button.
 */
export function FactList({
  facts,
  highlights = [],
  onAddToNote,
}: {
  facts: PilotFact[];
  /** Labels the model wants at the top right now. Empty before it has spoken. */
  highlights?: string[];
  onAddToNote?: (text: string) => void;
}) {
  const t = useT();

  // Highlighted first, in the order they were given, then the rest. Nothing is
  // hidden: a fact that did not make the shortlist is still the answer to a
  // question the caller might ask in the next ten seconds.
  const ranked = [
    ...highlights.map((label) => facts.find((f) => f.label === label)).filter((f): f is PilotFact => !!f),
    ...facts.filter((f) => !highlights.includes(f.label)),
  ];

  return (
    <ul className="mt-2 flex flex-col gap-1.5">
      {ranked.map((fact) => {
        const Icon = SOURCE_ICON[fact.source] ?? Globe;
        const highlighted = highlights.includes(fact.label);
        return (
          <li key={`${fact.source}-${fact.label}`} className={cn("crm-cp-fact", highlighted && "is-hot")}>
            <Icon className="mt-[3px] size-3 shrink-0 opacity-70" />
            <span className="min-w-0">
              <b className="block text-[11px] font-[650] uppercase tracking-[0.04em] text-muted-foreground">
                {fact.label}
              </b>
              <span className="block text-[12.5px] leading-[1.45]">{fact.value}</span>
            </span>
            {onAddToNote ? (
              <button
                type="button"
                className="crm-cp-add is-inline"
                onClick={() => onAddToNote(`${fact.label}: ${fact.value}`)}
                title={t("calls.pilot.addToNote")}
                aria-label={t("calls.pilot.addToNote")}
              >
                <NotebookPen className="size-3" />
              </button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
